/**
 * Kho, ngưỡng nhập, giao hàng và luật chống kẹt.
 *
 * - Ngưỡng nhập: mặc định P09, chỉnh chung (nguongChung) hoặc ghi đè từng món.
 * - Khi tồn < ngưỡng và đủ tiền: đặt đơn refill đầy sức chứa (M05 × cấp),
 *   giao sau M04 giờ game.
 * - Chống kẹt (master-plan 3.3): nếu không đủ tiền mua 1 món rẻ nhất (đang mở)
 *   và kho các món mở trống → nhà cung cấp cho nợ 1 lô đồ uống rẻ nhất
 *   (đầy sức chứa), trừ dần M15 lãi gộp vào khoản nợ.
 */
import { layThamSo, layThamSoMoPhong } from './du-lieu.js';
import type { DuLieuGame, MatHang, TrangThaiGame } from './types.js';
import { xaoTron, type HamRng } from './rng.js';

/** Sức chứa kho mỗi món = M05 × cấp cửa hàng. */
export function sucChuaKho(state: TrangThaiGame, duLieu: DuLieuGame): number {
  return layThamSoMoPhong(duLieu, 'M05') * state.capCuaHang;
}

/** Ngưỡng nhập hiệu dụng của một món: ghi đè riêng > ngưỡng chung > P09. */
export function nguongNhap(ma: string, state: TrangThaiGame, duLieu: DuLieuGame): number {
  const rieng = state.nguongRieng[ma];
  if (rieng !== undefined) return rieng;
  if (state.nguongChung !== undefined) return state.nguongChung;
  return layThamSo(duLieu, 'P09');
}

/** Danh sách mặt hàng đã mở theo cấp cửa hàng. */
export function matHangMo(state: TrangThaiGame, duLieu: DuLieuGame): MatHang[] {
  return duLieu.matHang.filter((m) => m.capMo <= state.capCuaHang);
}

/** Tồn kho hiện tại (0 nếu chưa có mục). */
export function tonKhoHienTai(ma: string, state: TrangThaiGame): number {
  return state.kho[ma]?.ton ?? 0;
}

/**
 * Trừ 1 đơn vị kho cho một lượt mua thuộc `nhom`.
 * Chọn ngẫu nhiên trong các món ĐANG MỞ của nhóm đó; nếu nhóm không có món
 * mở nào thì trừ vào món mở bất kỳ (chỉ để phản ánh tồn kho vật lý).
 * Trả về mã món đã trừ, hoặc null khi cả nhóm hết hàng (khách bỏ đi).
 */
export function truKho(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  rng: HamRng,
  nhom: string,
): string | null {
  const mo = matHangMo(state, duLieu);
  let ungVien = mo.filter((m) => m.nhom === nhom);
  if (ungVien.length === 0) ungVien = mo;
  if (ungVien.length === 0) return null;
  for (const m of xaoTron(ungVien, rng)) {
    const muc = state.kho[m.ma];
    if (muc && muc.ton >= 1) {
      muc.ton -= 1;
      return m.ma;
    }
  }
  return null;
}

/** Tổng tồn kho các món đang mở. */
export function tongTonKhoMo(state: TrangThaiGame, duLieu: DuLieuGame): number {
  return matHangMo(state, duLieu).reduce((s, m) => s + tonKhoHienTai(m.ma, state), 0);
}

/** Giá vốn rẻ nhất trong các món đang mở. */
export function giaVonReNhat(state: TrangThaiGame, duLieu: DuLieuGame): number {
  const mo = matHangMo(state, duLieu);
  if (mo.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...mo.map((m) => m.giaVon));
}

/**
 * Đặt đơn nhập tự động cho mọi món dưới ngưỡng.
 * Mỗi món chỉ có 1 đơn đang giao; chỉ đặt khi đủ tiền.
 * Lượng đặt = bù đúng ngưỡng (S = ngưỡng): giữ vốn lưu động ổn định,
 * khớp giả định bảng tính (lãi = tiền mặt). Sức chứa kho (M05 × cấp)
 * chỉ làm trần an toàn (lô chống kẹt), không phải mức đặt hàng.
 * Trả về số đơn đã đặt trong lần này.
 */
export function datHangTuDong(state: TrangThaiGame, duLieu: DuLieuGame): number {
  const thoiGianGiao = layThamSoMoPhong(duLieu, 'M04');
  let soDon = 0;
  for (const m of matHangMo(state, duLieu)) {
    const ton = tonKhoHienTai(m.ma, state);
    const nguong = nguongNhap(m.ma, state, duLieu);
    if (ton >= nguong) continue;
    if (state.donHang.some((d) => d.ma === m.ma)) continue;
    const soLuong = Math.max(nguong - ton, 0);
    if (soLuong <= 0) continue;
    const chiPhi = soLuong * m.giaVon;
    if (state.xu < chiPhi) continue; // chưa đủ tiền — chờ kỳ sau
    state.xu -= chiPhi;
    state.donHang.push({ ma: m.ma, soLuong, gioDen: state.tongGioGame + thoiGianGiao });
    state.thongKe.soDonHangDaDat += 1;
    soDon += 1;
  }
  return soDon;
}

/** Nhận các đơn đến hạn. Trả về số đơn đã giao. */
export function nhanGiaoHang(state: TrangThaiGame): number {
  let soDon = 0;
  const conLai: typeof state.donHang = [];
  for (const d of state.donHang) {
    if (d.gioDen <= state.tongGioGame) {
      const muc = state.kho[d.ma];
      if (muc) {
        muc.ton += d.soLuong;
      } else {
        state.kho[d.ma] = { ton: d.soLuong };
      }
      state.thongKe.soDonHangDaGiao += 1;
      soDon += 1;
    } else {
      conLai.push(d);
    }
  }
  state.donHang = conLai;
  return soDon;
}

/**
 * Điều kiện chống kẹt: không đủ tiền mua 1 món rẻ nhất (đang mở)
 * VÀ kho các món mở đã trống. Không kích hoạt khi đang còn nợ.
 */
export function kiemTraChongKet(state: TrangThaiGame, duLieu: DuLieuGame): boolean {
  if (state.khoanNo > 0) return false;
  if (state.xu >= giaVonReNhat(state, duLieu)) return false;
  return tongTonKhoMo(state, duLieu) <= 0;
}

/** Món dùng cho lô nợ: đồ uống rẻ nhất trong các món đang mở. */
export function monChoNo(state: TrangThaiGame, duLieu: DuLieuGame): MatHang {
  const ungVien = matHangMo(state, duLieu).filter((m) => m.nhom === 'Đồ uống');
  const danhSach = ungVien.length > 0 ? ungVien : matHangMo(state, duLieu);
  if (danhSach.length === 0) throw new Error('Không có mặt hàng nào đang mở để cho nợ.');
  return danhSach.reduce((a, b) => (b.giaVon < a.giaVon ? b : a));
}

/**
 * Nhận 1 lô nợ: đầy sức chứa món cho nợ, ghi nợ = số lượng × giá vốn.
 * Trả về { ma, soLuong } của lô nợ.
 */
export function nhanLoNo(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
): { ma: string; soLuong: number } {
  const mon = monChoNo(state, duLieu);
  const soLuong = sucChuaKho(state, duLieu);
  const muc = state.kho[mon.ma];
  if (muc) {
    muc.ton += soLuong;
  } else {
    state.kho[mon.ma] = { ton: soLuong };
  }
  state.khoanNo += soLuong * mon.giaVon;
  return { ma: mon.ma, soLuong };
}

/**
 * Trừ nợ từ lãi gộp của tick: tối đa M15 × lãi gộp.
 * Trả về số xu đã trừ vào nợ.
 */
export function traNoTuLaiGop(
  laiGopTick: number,
  state: TrangThaiGame,
  duLieu: DuLieuGame,
): number {
  if (state.khoanNo <= 0 || laiGopTick <= 0) return 0;
  const tyLe = layThamSoMoPhong(duLieu, 'M15');
  const tra = Math.min(state.khoanNo, laiGopTick * tyLe);
  state.khoanNo -= tra;
  return tra;
}
